import { Form, Formik } from 'formik';
import { ModalStatusType } from '../../types/common';
import ModalComponent from '../../components/modal/modal';
import ModalBodyComponent from '../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../components/modal/modalFooterComponent';
import FormSelect from '../../components/dropdown/formSelect';
import {
  useAdvanceBoolean,
  useGetCompanyProfileDetails,
  useGetCurrentTenantInfo,
  useGetCustomerSuccessCompanies,
  useGetSandboxSwitchable,
  useGetSwitchableByRole,
  useWealthlaneCustomerApi,
} from '../../hooks';
import AlertPopup from '../../components/alert-popup';
import { useEffect, useRef, useState } from 'react';
import AuthService from '../../services/authService';
import useInstance from '../../hooks/custom-hooks/useInstance';
import _ from 'lodash';
import useGetCustomerUserDetails from '../../hooks/get/useGetCustomerUserDetails';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../auth/authSlice';
import useGetSwitchableRoles from '../../hooks/get/useGetSwitchableRoles';
import useGetSwitchableCompaniesForSwitchPage from '../../hooks/get/useGetSwitchableCompaniesForSwitchPage';
import useGetCustomerPersonalDetails from '../../hooks/get/useGetCustomerPersonalDetails';
import { useWealthlaneIdentityApi } from '../../hooks/useWealthlaneIdentityApi';
import usePermissionCheck from '../../hooks/custom-hooks/usePermissionCheck';

interface NavbarSwitchModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading?: boolean;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  rowData?: any;
  domId?: string;
}

const NavbarSwitchModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  setModalStatus,
  rowData,
  domId
}: NavbarSwitchModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { handleRequest: handleRequestIdentity } = useWealthlaneIdentityApi();
  const isAuth = useSelector(selectIsAuthenticated) || new AuthService().isAuthenticated();
  const { getTenantById } = useInstance();

  const hasSwitchCompanyPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'SwitchCompany',
  });

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>();
  const [companyName, setCompanyName] = useState<string>();
  const [sandboxName, setSandboxName] = useState<string>();
  const [switchableRole, setSwitchableRole] = useState<string>();

  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue: selectedInstance,
    setCustomValue: setSelectedInstance,
  } = useAdvanceBoolean<string>(false);
  const { loading, onSwitch } = useInstance({ onClose: closeConfirmModal });
  const { data: userDetails } = useGetCustomerUserDetails(handleRequest, isAuth);
  const { data: switchableRoles } = useGetSwitchableRoles(handleRequest, {
    destinationCompanyId: selectedCompanyId,
    enabled: !_.isEmpty(selectedCompanyId) && !_.isEmpty(selectedInstance),
  });

  const { data: currentTenant, isLoading: isCurrentTenantLoading } =
    useGetCurrentTenantInfo(handleRequest);
  const { getRoleId } = useInstance();

  const { data: customerCompanies, isLoading: isCustomerSuccessLoading } =
    useGetSwitchableCompaniesForSwitchPage(handleRequest);
  const { data: personalDetails } = useGetCustomerPersonalDetails(handleRequest);

  const { data: companyDetails } = useGetCompanyProfileDetails(
    handleRequest,
    new AuthService().isAuthenticated()
  );

  const { data: switchableOptionType, isLoading: isSwitchableLoading } = useGetSwitchableByRole(
    handleRequestIdentity,
    {
      companyId: selectedCompanyId,
      roleId: switchableRole,
      onSuccess: (data) => {
        if (currentTenant?.name === 'Main' || selectedCompanyId !== companyDetails?.companyId) {
          const instance = data?.find((item) => item.label === 'Main');
          setSandboxName(instance?.label);
          setSelectedInstance(instance?.value);
        } else {
          setSandboxName(currentTenant?.name ?? '');
          setSelectedInstance(currentTenant?.id);
        }
      },
    }
  );

  const onPrimaryClick = (values: Record<string, any>): void => {
    onSwitch({
      id: selectedInstance,
      isCompanyChanged: companyDetails?.companyId !== selectedCompanyId,
      isHomeCompany:
        userDetails?.companyId === values?.companyName &&
        switchableOptionType?.find((item) => item?.value === selectedInstance)?.label === 'Main',
      switchableRole: values?.switchableRole,
      instanceName: values?.instanceName || sandboxName,
      instanceId: values?.instanceId || selectedInstance,
      companyId: selectedCompanyId,
    });
  };

  const onAdjustOverflow = () => {
    setTimeout(() => {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    });
  };

  const hasSwitchableRoles = Boolean(switchableRoles?.length);

  useEffect(() => {
    setSandboxName(currentTenant?.name ?? '');
  }, [currentTenant]);

  useEffect(() => {
    setSelectedCompanyId(companyDetails?.companyId);
    setCompanyName(companyDetails?.companyName ?? '');
  }, [companyDetails]);

  useEffect(() => {
    if (switchableRoles && selectedCompanyId === companyDetails?.companyId) {
      const savedRoleId = getRoleId();
      if (savedRoleId && switchableRoles.some((role) => role.value === savedRoleId)) {
        setSwitchableRole(savedRoleId);
      } else if (switchableRoles.length === 1) {
        setSwitchableRole(String(switchableRoles[0]?.value || ''));
      }
    } else if (switchableRoles?.length === 1) {
      setSwitchableRole(String(switchableRoles[0]?.value || ''));
    } else {
      setSwitchableRole('');
    }
  }, [switchableRoles, selectedCompanyId, companyDetails?.companyId]);

  const sandboxChanged = useRef(false);
  const goToOwnCompany =
    personalDetails?.companyId === selectedCompanyId &&
    selectedCompanyId !== companyDetails?.companyId;

  const isLoading = isSwitchableLoading || isCurrentTenantLoading;

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          companyName: selectedCompanyId,
          instanceId: selectedInstance ?? '',
          switchableRole: switchableRole,
        }}
        onSubmit={(values) => {
          openConfirmModal();
        }}>
        {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
          return (
            <ModalComponent
              id={domId}
              show={isModalOpen}
              closeModal={closeModal}
              size='sm'
              title={`Switch Instance`}
              children={
                <Form id='switchInstanceForm'>
                  <ModalBodyComponent id='switchModalBody'>
                    <FormSelect
                      type='text'
                      label='Company'
                      name='companyName'
                      id='companyName'
                      viewName='companyName'
                      onChange={(e: any) => {
                        handleChange(e);
                        sandboxChanged.current = false;
                        setSelectedCompanyId(e);
                        setFieldValue('switchableRole', '');
                        setSwitchableRole('');
                        setCompanyName(
                          customerCompanies?.find((item) => item?.value === e)?.label as string
                        );
                      }}
                      errors={errors}
                      touched={touched}
                      options={customerCompanies}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      required
                      isLoading={isCustomerSuccessLoading}
                    />

                    <FormSelect
                      type='text'
                      label='Instance'
                      name='instanceId'
                      id='instanceId'
                      viewName='instanceId'
                      onChange={(e: any) => {
                        const name = switchableOptionType?.find((item) => item?.value === e)?.label;
                        setSandboxName(name);
                        setSelectedInstance(e);
                        sandboxChanged.current = selectedCompanyId === companyDetails?.companyId;
                        handleChange(e);
                        setFieldValue('sandboxName', name);
                      }}
                      errors={errors}
                      touched={touched}
                      options={switchableOptionType}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      required
                      isLoading={isLoading}
                    />

                    {hasSwitchCompanyPermission &&
                      personalDetails?.companyId !== selectedCompanyId && (
                        <FormSelect
                          type='text'
                          label='Switch Roles'
                          name='switchableRole'
                          id='switchableRole'
                          onChange={(e: string) => {
                            handleChange(e);
                            setSwitchableRole(e);
                          }}
                          errors={errors}
                          touched={touched}
                          options={switchableRoles}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                          isLoading={isCustomerSuccessLoading}
                        />
                      )}
                  </ModalBodyComponent>
                  <ModalFooterComponent
                    modalStatus={modalStatus}
                    loading={false}
                    onCloseModal={() => {
                      closeModal();
                    }}
                    buttonTitle={'Go'}
                    icon={'bubble'}
                    onEditModal={() => {
                      setModalStatus('Edit');
                    }}
                    isLoading={isLoading || isSwitchableLoading || isCurrentTenantLoading}
                    id='switchModalFooter'
                    checkError={(() => {
                      const isInstanceSelected = !_.isEmpty(values.instanceId);
                      const isRoleSelected = !_.isEmpty(values.switchableRole);
                      const isSwitchingCompany = selectedCompanyId !== companyDetails?.companyId;
                      const isGoingBackToOwnCompany =
                        personalDetails?.companyId === selectedCompanyId;
                      const isSwitchedUser = Boolean(getTenantById()?.tenantId);

                      if (isSwitchingCompany && !hasSwitchCompanyPermission) {
                        return true;
                      }

                      // If going back to own company, only check instance selection
                      if (isGoingBackToOwnCompany) {
                        return (
                          !isInstanceSelected || _.isEqual(values?.instanceId, currentTenant?.id)
                        );
                      }

                      // For switching instance in current company (not own company) as switched user
                      // OR switching to other company: require both instance and role selection
                      if ((isSwitchedUser && !isGoingBackToOwnCompany) || isSwitchingCompany) {
                        return !isInstanceSelected || !isRoleSelected;
                      }

                      // For same company: only require instance selection
                      return (
                        !isInstanceSelected || _.isEqual(values?.instanceId, currentTenant?.id)
                      );
                    })()}
                  // !goToOwnCompany &&
                  // (sandboxChanged.current
                  //   ? false
                  //   : _.isEqual(values?.sandboxType, currentTenant?.id) || !hasSwitchableRoles)
                  />
                  {isConfirmOpen && (
                    <AlertPopup
                      isShow={isConfirmOpen}
                      closeModal={() => {
                        onAdjustOverflow();
                        closeConfirmModal();
                      }}
                      customTitle={`You are about to change to ${companyName} Company, ${sandboxName} instance. Are you sure?`}
                      handleSubmit={() => {
                        onAdjustOverflow();
                        onPrimaryClick(values);
                      }}
                      loading={loading}
                      id='switchConfirmationModal'
                    />
                  )}
                </Form>
              }
            />
          );
        }}
      </Formik>
    </>
  );
};

export default NavbarSwitchModal;
